import React, { useState, useImperativeHandle, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { copyToClipboard } from '../../utils/clipboard';
import copyClipboardIcon from '../../images/copy-clipboard-icon.svg';
import './CopyToClipboardButton.scss';

/**
 * CopyToClipboardButton
 * A reusable button component that copies text to the clipboard and displays a "Copied" confirmation bubble.
 * Exposes handleCopy method via ref for external triggering.
 *
 * @param {Object} props - Component props
 * @param {string} props.text - The text to copy to the clipboard
 * @param {string} [props.className] - Additional CSS class names for the wrapper
 * @param {string} [props.bubbleText] - Text to display in the confirmation bubble (default: "Copied")
 * @param {number} [props.bubbleDuration] - Duration in milliseconds to show the bubble (default: 2000)
 * @param {function} [props.onCopy] - Optional callback function called after successful copy
 * @param {function} [props.onError] - Optional callback function called if copy fails
 * @param {React.Ref} ref - Ref to expose handleCopy method
 * @returns {JSX.Element} React element for the copy button
 */
const CopyToClipboardButton = forwardRef(({
    text,
    className = '',
    bubbleText = 'Copied',
    bubbleDuration = 2000,
    onCopy,
    onError
}, ref) => {
    const [showCopied, setShowCopied] = useState(false);

    /**
     * Handles the copy action: copies text to clipboard and shows confirmation bubble
     */
    const handleCopy = async () => {
        if (!text) {
            return;
        }

        try {
            await copyToClipboard(text);
            setShowCopied(true);

            // Call optional onCopy callback
            if (onCopy) {
                onCopy(text);
            }

            // Hide bubble after specified duration
            setTimeout(() => {
                setShowCopied(false);
            }, bubbleDuration);
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);

            // Call optional onError callback
            if (onError) {
                onError(error);
            }
        }
    };

    // Expose handleCopy method via ref for external triggering
    useImperativeHandle(ref, () => ({
        handleCopy
    }));

    if (!text) {
        return null;
    }

    const wrapperClassName = ['copy-to-clipboard-button', className]
        .filter((name) => name.length > 0)
        .join(' ');

    return (
        <div className={wrapperClassName}>
            <div className="clipboard-icon-wrapper">
                <img
                    src={copyClipboardIcon}
                    alt="Copy to Clipboard"
                    onClick={handleCopy}
                />
                {showCopied && (
                    <span className="copied-bubble">{bubbleText}</span>
                )}
            </div>
        </div>
    );
});

CopyToClipboardButton.propTypes = {
    text: PropTypes.string.isRequired,
    className: PropTypes.string,
    bubbleText: PropTypes.string,
    bubbleDuration: PropTypes.number,
    onCopy: PropTypes.func,
    onError: PropTypes.func
};

CopyToClipboardButton.displayName = 'CopyToClipboardButton';

export default CopyToClipboardButton;

